/**
 * Shared 409 TENANT_NOT_PRICED message + link (TMMPDM-0021). TMMPDM-0025 (tmm-api 0082): 409
 * TENANT_CURRENCY_NOT_PRICED renders beside it — the tenant's currency isn't its reseller's
 * rate-card currency — with the same agreement link. The code says which; nothing is inferred.
 */
import { Alert, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';

export type NotPricedCode = 'TENANT_NOT_PRICED' | 'TENANT_CURRENCY_NOT_PRICED';

interface TenantNotPricedAlertProps {
  resellerId: number | null;
  resellerName?: string | null | undefined;
  /** Which 409 was answered; default TENANT_NOT_PRICED. */
  code?: NotPricedCode | undefined;
  /** The tenant's currency, for the TENANT_CURRENCY_NOT_PRICED message. */
  currency?: string | undefined;
}

export function TenantNotPricedAlert({
  resellerId,
  resellerName,
  code = 'TENANT_NOT_PRICED',
  currency,
}: TenantNotPricedAlertProps) {
  const { t } = useCopy();
  return (
    <Alert severity="error">
      {code === 'TENANT_CURRENCY_NOT_PRICED'
        ? t(
            'tenants.notPriced.currencyMessage',
            "This tenant's currency ({{currency}}) isn't priced by its reseller's rate card.",
            { currency: currency ?? '—' },
          )
        : t(
            'tenants.notPriced.message',
            "This tenant can't sell yet: its reseller has no agreement.",
          )}{' '}
      {resellerId !== null ? (
        <Link
          component={RouterLink}
          to={`/partners/${resellerId}?tab=agreement`}
          color="inherit"
          underline="always"
        >
          {t('tenants.notPriced.openAgreement', "Open {{resellerName}}'s agreement", {
            resellerName: resellerName ?? `#${resellerId}`,
          })}
        </Link>
      ) : null}
    </Alert>
  );
}

import { ApiError } from '@/api/errors';

/** The not-priced 409 an action answered, else `null`. */
export function notPricedCode(error: unknown): NotPricedCode | null {
  if (!(error instanceof ApiError)) return null;
  const code = String(error.code);
  return code === 'TENANT_NOT_PRICED' || code === 'TENANT_CURRENCY_NOT_PRICED' ? code : null;
}
